import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {CaretDown, CaretUp, Power, Trash, X} from '../../icons';
import {Spinner} from '../../Spinner';
import {BusinessUnitEditApi} from '../../../api/businessUnitEditApi';
import {lineSavedMessage} from '../../../utils/lineRecalculation';
import {activateLine, deactivateLine, deleteLine} from './lineManageActions';
import {BudgetConfiguration} from '../../business_units/accounts/BudgetConfiguration';
import {InformativeBudgetNotice} from '../../elements/InformativeBudgetNotice';
import {GoalConfiguration} from '../../business_units/global_info/GoalConfiguration';
import {SaveButton} from '../../business_units/SaveButton';
import {AdAccountsActionDataHandler} from '../../AdAccountsActionDataHandler';
import {BU_PERIOD_TEMPORARY, getBuPeriodName} from '../../../periods';
import {
    BudgetHelper,
    PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE,
} from '../../../utils/BudgetHelper';
import {
    ALLOCATED_AMOUNT_TYPE_SUM_OF_ADGROUPS,
    ALLOCATED_AMOUNT_TYPE_SUM_OF_CAMPAIGNS,
} from '../../business_units/BudgetTypes';
import {formatCompactMoney, spendPct} from './panelFormatters';
import {LineSpendBar} from './LineVisuals';
import {anchorAttr} from '../../../guides/anchors';
import './LineEditDrawer.scss';

// La clase que desplaza los banners globales mientras el drawer está abierto (mismo mecanismo que
// el panel de feedback: el z-index NO tiene solución en los dos marcos — ver FeedbackPanel.jsx).
const BODY_OPEN_CLASS = 'line-edit-drawer-open';

/**
 * Panel lateral de edición de una línea (card gtAL5axI, paso 2): presupuesto y objetivo desde la
 * fila, sin viajar a otra pantalla. Navega ↑↓ entre las líneas de la lista FILTRADA sin cerrarse —
 * cargar doce presupuestos son doce ediciones, no doce viajes.
 *
 * Reusa los forms de la ficha de la línea SIN MODIFICARLOS (`BudgetConfiguration` +
 * `GoalConfiguration`, regla "v2 es aditivo") contra el MISMO endpoint de la edición completa:
 * `GET/PUT business-unit/:id/edit`. Como ese PUT espera el paquete ENTERO, el drawer lee primero y
 * devuelve intacto todo lo que no toca (nombre, schedule, objetivo 2, período) — el postData
 * replica el de `BusinessUnitGlobalInfoForm.postData()`, incluida la re-derivación de las metas
 * parciales en modo daily-range cuando cambia el monto.
 *
 * El período se muestra pero no se edita acá: el PUT ni siquiera admite cambiar el TIPO de período,
 * y las fechas de una línea eventual son una edición de estructura, no del día a día — para eso
 * está "Abrir la línea completa".
 *
 * El objetivo editable es el PRINCIPAL (prioridad 1): es el que muestran la fila y el chip. El
 * objetivo 2 viaja intacto y se edita en la línea completa.
 */
class LineEditDrawerClass extends Component {
    constructor(props) {
        super(props);
        this.state = this.initialState();

        this.handleBudgetChange = this.handleBudgetChange.bind(this);
        this.handleRolloverChange = this.handleRolloverChange.bind(this);
        this.handleGoalChange = this.handleGoalChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.requestClose = this.requestClose.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.confirmDiscard = this.confirmDiscard.bind(this);
        this.cancelDiscard = this.cancelDiscard.bind(this);
        this.loadData = this.loadData.bind(this);
        this.manageRef = React.createRef();
    }

    initialState() {
        return {
            loading: true,
            failed: false,
            saving: false,
            hasChanges: false,
            // La acción que espera el "¿descartar los cambios?": null | {kind: 'close'|'navigate', delta}
            pendingAction: null,
            // Gestión de la línea (el reemplazo del StatusToggl de Monitoreo): una acción en vuelo
            // a la vez, y los dos confirms que la preceden.
            managing: false,
            confirmingDelete: false,
            confirmingLinkAccounts: false,
            // Se entró al drawer desde el gesto «gestionar la línea» de la fila: la vista aterriza
            // en la sección de abajo y se la señala un momento.
            manageHighlighted: false,
            bu: null,
            budgetCurrency: null,
            allocatedBudget: null,
            budgetPartialGoals: [],
            partialGoalsInputMode: null,
            partialGoalsInputDefinition: null,
            rollover: false,
            goal1: null,
        };
    }

    componentDidMount() {
        document.body.classList.add(BODY_OPEN_CLASS);
        document.addEventListener('keydown', this.handleKeyDown);
        this.loadData();
    }

    componentWillUnmount() {
        document.body.classList.remove(BODY_OPEN_CLASS);
        document.removeEventListener('keydown', this.handleKeyDown);
        clearTimeout(this.highlightTimer);
        (this.settleTimers || []).forEach(clearTimeout);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.line && this.props.line && prevProps.line.id !== this.props.line.id) {
            this.setState(this.initialState(), this.loadData);
        }
    }

    loadData() {
        const lineId = this.props.line.id;
        this.setState({loading: true, failed: false});
        BusinessUnitEditApi.get(lineId).then((response) => {
            // El usuario pudo navegar a otra línea mientras viajaba el GET: esa respuesta ya no es
            // de la línea abierta y pisar el estado con ella mezclaría dos líneas.
            if (!this.props.line || this.props.line.id !== lineId) return;
            if (!response || response.status !== 'OK' || !response.data) {
                this.setState({loading: false, failed: true});
                return;
            }
            const data = response.data;
            this.setState({
                loading: false,
                failed: false,
                hasChanges: false,
                bu: data,
                budgetCurrency: data.budget_currency,
                allocatedBudget: data.allocated_budget,
                budgetPartialGoals: this.partialGoalsWithDates(data.budget_partial_goals),
                partialGoalsInputMode: BudgetHelper.deriveInputModeFromDefinition(
                    data.budget_partial_goals_input_definition),
                partialGoalsInputDefinition: data.budget_partial_goals_input_definition || null,
                rollover: data.rollover,
                goal1: this.goalToFormShape(data.goal_1),
            }, this.focusRequestedSection);
        });
    }

    /**
     * Llevar la vista a «Gestión» cuando se entró al drawer PARA gestionar.
     *
     * Desactivar y eliminar viven al pie del formulario —después de Presupuesto y Objetivo, que son
     * las dos secciones largas—, así que sin scroll no se ven: Pablo buscó cómo eliminar una línea
     * desde la 2.1 y concluyó que no se podía. La acción existía desde el principio; lo que no
     * existía era el camino hasta ella.
     *
     * ⚠️ **El scroll es instantáneo y se REPITE, y las dos cosas se decidieron en pantalla.** Cuando
     * llega el GET el formulario todavía no terminó de crecer: `BudgetConfiguration` y
     * `GoalConfiguration` montan sus propios controles y traen sus propios datos, así que la sección
     * se va corriendo hacia abajo durante los ~2 s siguientes. Con `behavior: 'smooth'` la animación
     * salía hacia un destino que se movía y el usuario veía el formulario desde arriba dos segundos
     * —justo el problema que este gesto viene a resolver—. Un solo `scrollIntoView` no alcanza por
     * la misma razón: hay que volver a pedirlo cuando el contenido ya se asentó.
     *
     * El realce se enciende con el PRIMER scroll, no con el último. Con el scroll instantáneo la
     * vista aterriza en ese mismo instante (medido: la sección queda visible en el tick en que llega
     * el GET), así que atarlo al último reintento lo hacía aparecer casi un segundo DESPUÉS de que
     * el usuario ya estaba mirando la sección — un destello tardío se lee como «algo acaba de
     * cambiar», que es lo contrario de «aterrizaste acá».
     */
    focusRequestedSection = () => {
        if (this.props.focusSection !== 'manage') return;
        this.scrollToManage();
        this.setState({manageHighlighted: true});
        // Dos reintentos: uno para el frame siguiente al montaje de los hijos, y otro cuando ya
        // resolvieron sus fetches. Medido en pantalla: sin ellos la sección queda a mitad.
        this.settleTimers = [
            setTimeout(this.scrollToManage, 120),
            setTimeout(this.scrollToManage, 450),
        ];
        // Sobrevive con margen a los dos reintentos: apagarlo antes dejaría el último salto de la
        // vista sin ninguna señal de por qué se movió.
        this.highlightTimer = setTimeout(() => this.setState({manageHighlighted: false}), 2000);
    };

    scrollToManage = () => {
        const seccion = this.manageRef.current;
        // jsdom no implementa `scrollIntoView`: sin el guard, cualquier test que monte el drawer con
        // este foco se cae por el entorno y no por el componente.
        if (!seccion || typeof seccion.scrollIntoView !== 'function') return;
        seccion.scrollIntoView({block: 'center'});
    };

    // --- transformaciones (las MISMAS que `BusinessUnitGlobalInfoForm`) -------------------------

    goalToFormShape(goalData) {
        if (!goalData) return null;
        return {
            metricName: goalData.metric_name,
            value: goalData.value,
            metricConversionConfiguration: goalData.metric_conversion_configuration !== undefined
                ? goalData.metric_conversion_configuration : null,
        };
    }

    goalToPostShape(goalData) {
        if (!goalData) return null;
        return {
            metric_name: goalData.metricName,
            value: goalData.value,
            metric_conversion_configuration: goalData.metricConversionConfiguration !== undefined
                ? goalData.metricConversionConfiguration : null,
        };
    }

    partialGoalsWithDates(budgetPartialGoals) {
        if (!budgetPartialGoals) return [];
        return budgetPartialGoals.map((goal) => ({
            date: goal.date ? this.yyyymmddToDate(goal.date) : null,
            value: goal.value,
            type: goal.type,
        }));
    }

    partialGoalsWithYYYYMMDD(budgetPartialGoals) {
        return (budgetPartialGoals || []).map((goal) => ({
            date: goal.date ? this.dateToYYYYMMDD(goal.date) : null,
            value: goal.value,
            type: goal.type,
        }));
    }

    // Acepta `20261001` (las fechas de los tramos) y `2026-10-01`: así manda `business-unit/:id/edit`
    // el período (`period_*_date_yyyymmdd`). Con guiones, cortar por posición daba el 31/10/2025 y
    // el drawer de una línea eventual decía «31/10 – 31/10» (QA en producción del wizard, 27/09).
    yyyymmddToDate(yyyymmdd) {
        const text = String(yyyymmdd).replace(/\D/g, '');
        return new Date(parseInt(text.slice(0, 4), 10), parseInt(text.slice(4, 6), 10) - 1,
            parseInt(text.slice(6, 8), 10));
    }

    dateToYYYYMMDD(date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${date.getFullYear()}${month}${day}`;
    }

    // El paquete ENTERO de vuelta: lo editado acá + todo lo demás intacto desde el GET. Espeja
    // `BusinessUnitGlobalInfoForm.postData()` — si aquel contrato cambia, este también.
    buildPostData() {
        const bu = this.state.bu;
        const postData = {
            name: bu.name,
            rollover: this.state.rollover,
            budget_currency: this.state.budgetCurrency,
            budget_amount: this.state.allocatedBudget,
            goal_1: this.goalToPostShape(this.state.goal1),
            // El objetivo 2 no se edita acá: viaja como vino.
            goal_2: bu.goal_2 ? {
                metric_name: bu.goal_2.metric_name,
                value: bu.goal_2.value,
                metric_conversion_configuration: bu.goal_2.metric_conversion_configuration !== undefined
                    ? bu.goal_2.metric_conversion_configuration : null,
            } : null,
            schedule_configuration: bu.schedule_configuration_data,
        };

        if (this.state.partialGoalsInputMode === PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE) {
            // En daily-range las metas acumuladas DERIVAN del monto: si el drawer lo cambió, hay
            // que recalcularlas con el monto nuevo (igual que el form completo al guardar).
            const entries = (this.state.partialGoalsInputDefinition
                && this.state.partialGoalsInputDefinition.entries) || [];
            postData.budget_partial_goals = BudgetHelper.deriveCumulativeFromDailyRange(
                this.state.allocatedBudget, bu.period_first_date_yyyymmdd,
                bu.period_last_date_yyyymmdd, entries);
            postData.budget_partial_goals_input_definition =
                BudgetHelper.BudgetPartialGoalDailyRangeTranslateToSubmit(entries);
        } else {
            postData.budget_partial_goals = this.partialGoalsWithYYYYMMDD(this.state.budgetPartialGoals);
            postData.budget_partial_goals_input_definition = null;
        }

        if (bu.period_type_enum === BU_PERIOD_TEMPORARY
            && bu.period_first_date_yyyymmdd && bu.period_last_date_yyyymmdd) {
            postData.period_from_date = bu.period_first_date_yyyymmdd;
            postData.period_to_date = bu.period_last_date_yyyymmdd;
        }

        return postData;
    }

    // --- handlers -------------------------------------------------------------------------------

    handleBudgetChange(newCurrency, newAmount, newBudgetPartialGoals, partialGoalsConfig) {
        const update = {
            hasChanges: true,
            budgetCurrency: newCurrency,
            allocatedBudget: newAmount,
            budgetPartialGoals: newBudgetPartialGoals,
        };
        if (partialGoalsConfig) {
            update.partialGoalsInputMode = partialGoalsConfig.inputMode;
            update.partialGoalsInputDefinition = partialGoalsConfig.inputDefinition || null;
        }
        this.setState(update);
    }

    handleRolloverChange(value) {
        this.setState({hasChanges: true, rollover: value});
    }

    handleGoalChange(data) {
        this.setState({hasChanges: true, goal1: data});
    }

    handleSave() {
        if (this.state.saving) return;
        this.setState({saving: true});
        // El id se toma ANTES del pedido: `onSaved` le dice al padre qué línea se guardó, y a la
        // vuelta `this.props.line` ya puede ser otra.
        const lineId = this.props.line.id;
        BusinessUnitEditApi.update(lineId, this.buildPostData()).then((response) => {
            this.setState({saving: false});
            if (response && response.status === 'OK') {
                toast.success(lineSavedMessage(response, this.props.t,
                    this.props.t('client_panel.line_drawer_saved', 'Línea actualizada')));
                this.setState({hasChanges: false});
                // Refrescar el GET: la moneda "original" del aviso de conversión y las metas
                // derivadas tienen que reflejar lo recién guardado.
                this.loadData();
                if (this.props.onSaved) this.props.onSaved(lineId);
            } else {
                // El `error_message` del backend se muestra A PROPÓSITO (acá y en la gestión): los
                // 400 de estos endpoints son accionables y en español, y taparlos con un genérico
                // ya mordió en el media plan (2026-07-31). Son endpoints scopeados por agencia —
                // no hay mensaje cross-agency que filtrar.
                toast.error((response && response.error_message)
                    || this.props.t('client_panel.line_drawer_save_error', 'No pudimos guardar los cambios'));
            }
        });
    }

    // --- gestión de la línea (desactivar / reactivar / eliminar) --------------------------------
    // El reemplazo del `StatusToggl` de Monitoreo, que era el ÚNICO lugar donde existían estas
    // tres acciones (el Delete de la ficha de la BU está comentado).
    //
    // Desde el 2026-09-10 las escrituras NO viven acá: están en `lineManageActions.js`, porque el
    // menú `⋮` de la fila las ofrece también y son dos puertas a un solo camino de escritura (la
    // invariante que fijó la card IysOiDWB). Lo que queda en este archivo es lo que sí es del
    // drawer: cuándo se pregunta, dónde se dibuja el confirm y qué pasa después.

    isInactive() {
        return this.props.line.status === 'INACTIVE';
    }

    // El resultado de una escritura, con la UI que le corresponde a ESTA superficie.
    settleManaged = (result) => {
        this.setState({managing: false});
        if (result.needsLinkConfirm) {
            // El backend pide confirmación: las cuentas de la línea no están vinculadas al cliente
            // (mismo flujo que el modal de Monitoreo) — se pregunta y se reintenta.
            this.setState({confirmingLinkAccounts: true});
            return;
        }
        if (!result.ok) {
            toast.error(result.message);
            return;
        }
        toast.success(result.message);
        if (this.props.onSaved) this.props.onSaved();
        // La línea cambió de lista (activas ↔ inactivas) o dejó de existir: el drawer se cierra —
        // quedarse abierto sobre una fila que ya no está donde estaba desorienta más que el cierre.
        this.props.onClose();
    };

    // Desactivar va DIRECTO acá, sin confirmar, y desde el menú de la fila sí confirma. No es
    // incoherencia: en el drawer el nombre de la línea está en el encabezado y su consumo a la
    // vista, así que la intención ya está evidenciada; en la fila es un click sobre una lista.
    handleDeactivate = () => {
        if (this.state.managing) return;
        this.setState({managing: true});
        deactivateLine(this.props.line.id, this.props.t).then(this.settleManaged);
    };

    handleActivate = (addAccountsToClient = false) => {
        if (this.state.managing) return;
        this.setState({managing: true, confirmingLinkAccounts: false});
        activateLine(this.props.line.id, {addAccountsToClient}, this.props.t).then(this.settleManaged);
    };

    handleDelete = () => {
        if (this.state.managing) return;
        this.setState({managing: true, confirmingDelete: false});
        deleteLine(this.props.line.id, this.props.t).then(this.settleManaged);
    };

    // Cerrar, navegar y saltar a otra fila pasan por el mismo guard: con cambios sin guardar se
    // pregunta antes de descartar (perderlos en silencio por un ↑ o un lápiz es peor que una
    // pregunta).
    requestClose() {
        this.requestAction({kind: 'close'});
    }

    requestNavigate(delta) {
        this.requestAction({kind: 'navigate', delta});
    }

    // Lo consulta el shell cuando el usuario cambia de CLIENTE con el drawer abierto: ahí la
    // navegación ya ocurrió y no hay nada que cancelar, así que no se puede usar el guard de
    // descarte — lo único honesto es cerrar y avisar que se perdió lo tipeado.
    hasUnsavedChanges() {
        return !!this.state.hasChanges;
    }

    // El click en el lápiz de OTRA fila con el drawer abierto (C7 del review 25/08): el dueño del
    // `editingId` es el shell, así que le pide el salto por acá (vía ref) en vez de setearlo
    // directo — setearlo directo remontaba el estado del form y tiraba lo tipeado sin preguntar.
    requestSwitch(lineId) {
        this.requestAction({kind: 'switch', lineId});
    }

    requestAction(action) {
        if (this.state.hasChanges) {
            this.setState({pendingAction: action});
            return;
        }
        this.runAction(action);
    }

    runAction(action) {
        if (action.kind === 'close') this.props.onClose();
        else if (action.kind === 'navigate') this.props.onNavigate(action.delta);
        else if (action.kind === 'switch') this.props.onSwitchLine(action.lineId);
    }

    confirmDiscard() {
        const action = this.state.pendingAction;
        this.setState({pendingAction: null, hasChanges: false}, () => {
            if (action) this.runAction(action);
        });
    }

    cancelDiscard() {
        this.setState({pendingAction: null});
    }

    handleKeyDown(event) {
        if (event.key !== 'Escape') return;
        // Con el confirm de descarte abierto, Escape lo cancela (vuelve a editar); sin él, cierra
        // el drawer (pasando por el guard de cambios).
        if (this.state.pendingAction) {
            this.cancelDiscard();
            return;
        }
        this.requestClose();
    }

    // --- render ---------------------------------------------------------------------------------

    budgetIsReadOnly() {
        const bu = this.state.bu;
        return bu.budget_type === ALLOCATED_AMOUNT_TYPE_SUM_OF_CAMPAIGNS
            || bu.budget_type === ALLOCATED_AMOUNT_TYPE_SUM_OF_ADGROUPS;
    }

    // Con el monto bloqueado, decir por qué y dónde se carga: sin texto, el campo gris se leía como
    // una falla (cc#238, Visión Digital, 01/11).
    budgetFootnoteText() {
        const {t} = this.props;
        const bu = this.state.bu;
        if (bu.budget_type === ALLOCATED_AMOUNT_TYPE_SUM_OF_CAMPAIGNS)
            return t('business_unit_main_info.budget_sum_campaigns');
        if (bu.budget_type === ALLOCATED_AMOUNT_TYPE_SUM_OF_ADGROUPS)
            return t('business_unit_main_info.budget_sum_adgroups');
        return '';
    }

    renderHead() {
        const {t, line, position} = this.props;
        return (
            <div className="pled-head">
                <div className="pled-head__txt">
                    <h3 className="pled-head__name">{line.name}</h3>
                    {position && position.total > 1 && (
                        <span className="pled-head__pos">
                            {t('client_panel.line_drawer_position', '{{index}} de {{total}}', {
                                index: position.index + 1,
                                total: position.total,
                            })}
                        </span>
                    )}
                </div>
                <div className="pled-head__nav">
                    {this.props.onNavigate && (
                        <>
                            <button
                                type="button"
                                className="pled-icon-btn"
                                disabled={!position || position.index <= 0}
                                aria-label={t('client_panel.line_drawer_prev', 'Línea anterior')}
                                onClick={() => this.requestNavigate(-1)}
                            >
                                <CaretUp size={14} weight="bold"/>
                            </button>
                            <button
                                type="button"
                                className="pled-icon-btn"
                                disabled={!position || position.index < 0 || position.index >= position.total - 1}
                                aria-label={t('client_panel.line_drawer_next', 'Línea siguiente')}
                                onClick={() => this.requestNavigate(1)}
                            >
                                <CaretDown size={14} weight="bold"/>
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        className="pled-icon-btn"
                        aria-label={t('client_panel.line_drawer_close', 'Cerrar')}
                        onClick={this.requestClose}
                    >
                        <X size={14} weight="bold"/>
                    </button>
                </div>
            </div>
        );
    }

    /**
     * El consumo al lado del campo: un presupuesto se cambia MIRANDO lo invertido, el ritmo
     * esperado y hacia dónde cierra (paso 2 del handoff). Sale del payload del Panel que ya viene
     * en `line` — el drawer no vuelve a pedirlo.
     */
    renderContext() {
        const {t, line} = this.props;
        const budget = line.budget || {};
        const pct = spendPct(budget.spend, budget.allocated);

        if (!(Number(budget.allocated) > 0)) {
            return (
                <div className="pled-context">
                    {t('client_panel.line_drawer_no_budget_hint',
                        'Sin presupuesto no hay ritmo que evaluar: la línea queda fuera de las alertas y del semáforo.')}
                </div>
            );
        }

        const projection = line.projection || {};
        const projectionPct = Number(projection.vs_budget_pct);
        return (
            <div className="pled-context">
                <span
                    // El monto va interpolado con strong desde i18n para que la frase se pueda
                    // reordenar por idioma sin partirla en tres keys.
                    dangerouslySetInnerHTML={{
                        __html: t('client_panel.line_drawer_context',
                            'Va <strong>{{spend}}</strong> invertido — <strong>{{pct}}%</strong> del presupuesto, contra el <strong>{{expected}}%</strong> esperado a hoy.', {
                                spend: formatCompactMoney(line.currency, budget.spend),
                                pct: pct === null ? '—' : pct,
                                expected: budget.expected_pct === null || budget.expected_pct === undefined
                                    ? '—' : Math.round(Number(budget.expected_pct)),
                            }),
                    }}
                />
                <LineSpendBar line={line}/>
                {Number.isFinite(projectionPct) && (
                    <span className="pled-context__proj">
                        {t('client_panel.line_drawer_context_projection',
                            'Proyecta cerrar en {{pct}}% del presupuesto.', {pct: projectionPct})}
                    </span>
                )}
            </div>
        );
    }

    renderGoalHint() {
        const {t, line} = this.props;
        const goal = line.goal || {};
        if (!goal.defined) {
            return (
                <p className="pled-hint">
                    {/* La misma frase que el cartel del chip «Definir objetivo» (QA-VIS-SEGFE-17). */}
                    {t('client_panel.line_goal_why',
                        'Sin objetivo, tu IA sólo puede contar lo que gastó. Con uno, sabe si la línea va bien y tu tablero te avisa si se desvía.')}
                </p>
            );
        }
        if (goal.on_track !== true && goal.on_track !== false) {
            return (
                <p className="pled-hint">
                    {t('client_panel.line_drawer_goal_hint_stale',
                        'La línea no sincronizó en los últimos días: no se puede evaluar el objetivo.')}
                </p>
            );
        }
        return (
            <p className="pled-hint">
                {t('client_panel.line_drawer_goal_hint_current', 'Hoy va en {{current}} — {{state}}.', {
                    current: goal.current_value === null || goal.current_value === undefined
                        ? '—'
                        : Number(goal.current_value).toLocaleString('es-AR', {maximumFractionDigits: 2}),
                    state: goal.on_track
                        ? t('client_panel.line_drawer_goal_on_track', 'en ritmo')
                        : t('client_panel.line_drawer_goal_off_track', 'fuera de ritmo'),
                    interpolation: {escapeValue: false},
                })}
            </p>
        );
    }

    renderPeriod() {
        const {t} = this.props;
        const bu = this.state.bu;
        const isTemporary = bu.period_type_enum === BU_PERIOD_TEMPORARY;
        const label = isTemporary && bu.period_first_date_yyyymmdd && bu.period_last_date_yyyymmdd
            ? `${this.formatPeriodDate(bu.period_first_date_yyyymmdd)} – ${this.formatPeriodDate(bu.period_last_date_yyyymmdd)}`
            : getBuPeriodName(bu.period_type_enum, t);

        return (
            <div className="pled-period">
                <span className="pled-period__label">
                    {t('client_panel.line_drawer_period_label', 'Período')}
                </span>
                <span className="pled-period__value">{label}</span>
                {/* No se edita acá: el PUT no admite cambiar el tipo, y mover las fechas de una
                    eventual es una edición de estructura — vive en la línea completa. */}
                <span className="pled-period__note">
                    {t('client_panel.line_drawer_period_note', 'se cambia en la línea completa')}
                </span>
            </div>
        );
    }

    formatPeriodDate(yyyymmdd) {
        const date = this.yyyymmddToDate(yyyymmdd);
        return date.toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit'});
    }

    // La línea desactivada no sincroniza: el contexto de consumo y el estado del objetivo no
    // existen para ella — en su lugar se dice el estado, y la gestión ofrece reactivarla.
    renderInactiveNote() {
        const {t} = this.props;
        return (
            <div className="pled-context">
                {t('client_panel.line_drawer_inactive_note',
                    'Esta línea está desactivada: no sincroniza, no dispara alertas y no aparece en el Panel.')}
            </div>
        );
    }

    renderManage() {
        const {t} = this.props;
        const inactive = this.isInactive();

        return (
            <section className={`pled-sec pled-manage${this.state.manageHighlighted ? ' -focus' : ''}`}
                     ref={this.manageRef}>
                <h4 className="pled-sec__title">
                    {t('client_panel.line_manage_section', 'Gestión')}
                </h4>

                {this.state.confirmingLinkAccounts ? (
                    // El backend pidió confirmación para reactivar: las cuentas de la línea no
                    // están vinculadas al cliente (mismo flujo que el modal de Monitoreo).
                    <div className="pled-manage__confirm">
                        <p>{t('client_panel.line_manage_link_accounts_question',
                            'Las cuentas de esta línea no están vinculadas al cliente. ¿Vincularlas y reactivar la línea?')}</p>
                        <div className="pled-manage__row">
                            <button type="button" className="pnl-btn pnl-btn--primary pnl-btn--sm"
                                    disabled={this.state.managing}
                                    onClick={() => this.handleActivate(true)}>
                                {t('client_panel.line_manage_link_accounts_yes', 'Vincular y reactivar')}
                            </button>
                            <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm"
                                    onClick={() => this.setState({confirmingLinkAccounts: false})}>
                                {t('client_panel.line_manage_cancel', 'Cancelar')}
                            </button>
                        </div>
                    </div>
                ) : this.state.confirmingDelete ? (
                    <div className="pled-manage__confirm">
                        <p>{t('client_panel.line_manage_delete_question',
                            'Se eliminará la línea de seguimiento y su configuración. ¿Continuar?')}</p>
                        <div className="pled-manage__row">
                            <button type="button" className="pled-manage__danger"
                                    disabled={this.state.managing}
                                    onClick={this.handleDelete}>
                                {t('client_panel.line_manage_delete_confirm', 'Eliminar la línea')}
                            </button>
                            <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm"
                                    onClick={() => this.setState({confirmingDelete: false})}>
                                {t('client_panel.line_manage_cancel', 'Cancelar')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="pled-manage__row">
                        {/* Los dos son acciones sobre la línea y arrancaban como dos textos del
                            mismo peso: «¿Eliminar o Desactivar?» fue la pregunta de fede al
                            mirarlos. El ícono es lo que las separa de un vistazo, y son los mismos
                            que usa el menú de una fila del Listado v2 (Power / Trash). */}
                        {inactive ? (
                            <button type="button" className="pnl-btn pnl-btn--primary pnl-btn--sm"
                                    disabled={this.state.managing}
                                    onClick={() => this.handleActivate(false)}>
                                <Power size={15}/>
                                {t('client_panel.line_manage_activate', 'Reactivar esta línea')}
                            </button>
                        ) : (
                            <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm"
                                    disabled={this.state.managing}
                                    onClick={this.handleDeactivate}>
                                <Power size={15}/>
                                {t('client_panel.line_manage_deactivate', 'Desactivar esta línea')}
                            </button>
                        )}
                        <button type="button" className="pled-manage__delete"
                                disabled={this.state.managing}
                                onClick={() => this.setState({confirmingDelete: true})}>
                            <Trash size={14}/>
                            {t('client_panel.line_manage_delete', 'Eliminar')}
                        </button>
                    </div>
                )}

                {/* Desactivar es reversible; eliminar no — el hint lo dice antes del click. */}
                {!this.state.confirmingDelete && !this.state.confirmingLinkAccounts && (
                    <p className="pled-hint">
                        {inactive
                            ? t('client_panel.line_manage_hint_inactive',
                                'Reactivarla vuelve a sincronizarla y la devuelve al Panel.')
                            : t('client_panel.line_manage_hint_active',
                                'Desactivarla es reversible; eliminarla no.')}
                    </p>
                )}
            </section>
        );
    }

    renderForm() {
        const {t, line} = this.props;
        const bu = this.state.bu;

        return (
            <>
                {/* Los modificadores `--budget` / `--goal` son el gancho de `LineEditDrawer.scss`
                    para reencuadrar los formularios compartidos a los 424px del drawer: son la
                    única pista de QUÉ formulario está adentro que el CSS no puede deducir del
                    marcado ajeno. */}
                <section className="pled-sec pled-sec--budget" {...anchorAttr('line_drawer_budget')}>
                    <h4 className="pled-sec__title">
                        {t('client_panel.line_drawer_budget_section', 'Presupuesto')}
                    </h4>
                    {this.isInactive() ? this.renderInactiveNote() : this.renderContext()}
                    <BudgetConfiguration
                        key={`budget-${line.id}`}
                        canEditCurrency={bu.can_edit_currency}
                        budgetCurrency={this.state.budgetCurrency}
                        periodType={bu.period_type_enum}
                        periodFirstDate={bu.period_first_date_yyyymmdd
                            ? this.yyyymmddToDate(bu.period_first_date_yyyymmdd) : null}
                        periodLastDate={bu.period_last_date_yyyymmdd
                            ? this.yyyymmddToDate(bu.period_last_date_yyyymmdd) : null}
                        allocatedBudget={this.state.allocatedBudget}
                        isReadOnly={this.budgetIsReadOnly()}
                        lastPeriodBalance={bu.leftover_budget}
                        rollover={this.state.rollover}
                        onRolloverChange={this.handleRolloverChange}
                        onBudgetChange={this.handleBudgetChange}
                        budgetFootnoteText={this.budgetFootnoteText()}
                        linkedAccountCurrencies={bu.linked_account_currencies}
                        originalBudgetCurrency={bu.budget_currency}
                        allowBudgetPartialGoals={true}
                        budgetPartialGoals={this.state.budgetPartialGoals}
                        budgetPartialGoalsInputDefinition={this.state.partialGoalsInputDefinition}
                        compact={true}
                    />
                    {/* Si la línea es global y tiene presupuestos por campaña cargados (informativos,
                        card CvdQbnl2), se compara su suma con el global mientras se edita. Sin ninguno
                        cargado no se dibuja nada. */}
                    <InformativeBudgetNotice lineBudget={this.state.allocatedBudget}
                                             campaignsSum={bu.informative_campaigns_budget_sum}
                                             currency={this.state.budgetCurrency}/>
                    {this.renderPeriod()}
                </section>

                <section className="pled-sec pled-sec--goal">
                    <h4 className="pled-sec__title">
                        {t('client_panel.line_drawer_goal_section', 'Objetivo')}
                    </h4>
                    {!this.isInactive() && this.renderGoalHint()}
                    <GoalConfiguration
                        key={`goal-${line.id}`}
                        priority={1}
                        businessUnitId={line.id}
                        data={this.state.goal1}
                        adAccountsActionData={new AdAccountsActionDataHandler(
                            bu.business_unit_ad_accounts_actions_data, t)}
                        channels={line.channels}
                        onChange={this.handleGoalChange}
                    />
                </section>

                {this.renderManage()}
            </>
        );
    }

    renderBody() {
        const {t} = this.props;
        if (this.state.loading) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        if (this.state.failed || !this.state.bu) {
            return (
                <div className="pled-error">
                    <p>{t('client_panel.line_drawer_error', 'No pudimos cargar la línea.')}</p>
                    <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm" onClick={this.loadData}>
                        {t('client_panel.error_retry', 'Reintentar')}
                    </button>
                </div>
            );
        }
        return this.renderForm();
    }

    render() {
        const {line} = this.props;
        return (
            <>
                <div className="pled-scrim" onClick={this.requestClose}/>
                <aside className="pled-drawer" role="dialog" aria-modal="true" aria-label={line.name}>
                    {this.renderHead()}
                    <div className="pled-body">{this.renderBody()}</div>
                    {this.state.pendingAction ? this.renderDiscardConfirm() : this.renderFoot()}
                </aside>
            </>
        );
    }

    renderFoot() {
        const {t, line} = this.props;
        return (
            <div className="pled-foot">
                <SaveButton
                    text={t('client_panel.line_drawer_save', 'Guardar')}
                    isSaving={this.state.saving}
                    hasChanges={this.state.hasChanges}
                    onClick={this.handleSave}
                    guideAnchor={anchorAttr('line_drawer_save')}
                />
                {/* Las fuentes de datos (qué cuentas y campañas mide la línea) son lo que más se
                    va a querer tocar después del presupuesto y el objetivo, y hasta acá había que
                    pasar por la línea completa y buscar la pestaña. Va como link propio, antes del
                    genérico. Pedido de fede en el smoke del 26/08. */}
                <Link className="pled-foot__sources" to={`/business-unit/${line.id}/accounts`}>
                    {t('client_panel.line_drawer_sources', 'Fuentes de datos')}
                </Link>
                <Link className="pled-foot__full" to={`/business-unit/${line.id}/`}>
                    {t('client_panel.line_drawer_open_full', 'Abrir la línea completa →')}
                </Link>
            </div>
        );
    }

    // El confirm de descarte ocupa el lugar del pie: sin modal encima del drawer (el interplay
    // Escape/backdrop de un modal-dentro-del-overlay deja estados colgados) y la pregunta aparece
    // exactamente donde estaba el botón que no se apretó.
    renderDiscardConfirm() {
        const {t} = this.props;
        return (
            <div className="pled-foot pled-foot--confirm">
                <span className="pled-foot__question">
                    {t('client_panel.line_drawer_unsaved_question', 'Esta línea tiene cambios sin guardar.')}
                </span>
                <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm" onClick={this.confirmDiscard}>
                    {t('client_panel.line_drawer_unsaved_discard', 'Descartarlos')}
                </button>
                <button type="button" className="pnl-btn pnl-btn--primary pnl-btn--sm" onClick={this.cancelDiscard}>
                    {t('client_panel.line_drawer_unsaved_keep', 'Seguir editando')}
                </button>
            </div>
        );
    }
}

// `withRef`: el shell necesita la instancia para rutear el salto de fila por el guard de cambios
// (`requestSwitch`) — sin esto, el ref apunta al wrapper de i18n y el método no existe.
export const LineEditDrawer = withTranslation(undefined, {withRef: true})(LineEditDrawerClass);
export default LineEditDrawer;
